import * as React from "react"
import { ChevronRight } from "lucide-react"
import { Breadcrumb } from "@/components/brand-kit/breadcrumb"
import { brandsBreadcrumbs } from "@/lib/breadcrumbs"
import { accentColorFor } from "./types"
import type { IndexBrand } from "./types"

/**
 * Initials for the mini stack. Mirrors `deriveInitials` in `brand-logo.tsx`,
 * `featured-logo-bar.tsx` and `more-brands-strip.tsx`, so every small brand
 * tile on the site derives the same letters.
 */
function deriveInitials(name: string): string {
  const cleaned = (name || "").replace(/\(.*?\)/g, "").trim()
  const words = cleaned.split(/[\s&+\-']/).filter(Boolean)
  if (words.length === 0) return "?"
  if (words.length === 1) return words[0].charAt(0).toUpperCase()
  return (words[0].charAt(0) + (words[1] || "").charAt(0)).toUpperCase()
}

interface BrandsHeroProps {
  /** Up to 6 partner brands with logos — rendered in the overlapping pill stack. */
  miniStackBrands: IndexBrand[]
  /** Total brand count for the pill label ("174 brands tracked"). */
  totalCount: number
}

/**
 * Brands page editorial hero.
 *
 * Centered block under the global header with a subtle radial gradient
 * behind the title. Structure: topbar (breadcrumb + directory eyebrow) →
 * H1 with orange "Brands" accent → subtitle → mini logo-stack pill.
 *
 * Trust chips were removed per design feedback — the title + count pill
 * already do the work the chips were doing.
 *
 * Server component — pure render, no state, no client deps.
 */
export function BrandsHero({ miniStackBrands, totalCount }: BrandsHeroProps) {
  return (
    <section
      className="relative isolate mx-auto max-w-[1100px] pb-10 pt-8 text-center sm:pt-10 lg:pt-12"
      aria-labelledby="brands-hero-h1"
    >
      {/* Subtle ambient gradient behind the title — blue 7% top-left, orange
          6% top-right, fading to transparent before reaching the count pill. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(560px 320px at 22% 18%, rgba(4,105,240,0.07), transparent 60%), radial-gradient(620px 340px at 78% 18%, rgba(255,153,81,0.06), transparent 60%)",
        }}
      />

      {/* Topbar — breadcrumb · vertical divider · directory eyebrow */}
      <div className="mb-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[12.5px]">
        {/* ONE trail (lib/breadcrumbs.ts brandsBreadcrumbs) — app/brands/page.tsx
            emits the identical array as the BreadcrumbList (FRIDGE-269). */}
        <Breadcrumb
          trail={brandsBreadcrumbs()}
          className="font-medium text-gray-500"
          listClassName="flex items-center gap-1.5 list-none m-0 p-0"
          itemClassName="flex items-center gap-1.5"
          linkClassName="hover:text-gray-700"
          currentClassName="font-bold text-gray-800"
          separatorClassName="flex items-center text-gray-300"
          separator={<ChevronRight className="h-2.5 w-2.5" />}
        />
        <span aria-hidden className="hidden h-3.5 w-px bg-gray-300 sm:inline-block" />
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className="h-1.5 w-1.5 rounded-full"
            style={{
              background: "linear-gradient(135deg, #0469f0, #ff9951)",
              boxShadow: "0 0 0 3px rgba(4,105,240,0.12)",
            }}
          />
          <span className="text-[11.5px] font-extrabold uppercase tracking-[0.14em] text-[#0469f0]">
            Fridge.com Brand Directory
          </span>
        </div>
      </div>

      {/* H1 — sized so "Refrigerator & Freezer Brands" stays on one line in the
         max-w-[1100px] container at every breakpoint. Standard Tailwind scale
         (text-4xl..text-6xl = 36/48/60px) — `xl:text-7xl` is left off because
         60px already comfortably fills the 1100px container at xl+. */}
      <h1
        id="brands-hero-h1"
        className="text-4xl font-black leading-[1.02] tracking-tight text-gray-900 sm:text-5xl lg:text-6xl"
      >
        Refrigerator &amp; Freezer{" "}
        <span className="text-[#ff9951]">Brands</span>
      </h1>

      {/* Subtitle */}
      <p className="mx-auto mt-4 max-w-[620px] text-pretty text-[17px] leading-[1.5] text-gray-600">
        Every brand we track in one place — from heritage luxury to everyday value. Compare price
        ranges, product types, and find the right manufacturer for your kitchen.
      </p>

      {/* Mini brand-stack pill.
          FRIDGE-859: these tiles USED to render `logo_path` at 20x20 inside a
          32px circle. Measured in the browser at 375 and 1280, the six marks
          came out 20x2 to 20x10 CSS pixels — Frigidaire was 20x2, Samsung
          20x4, GE Appliances 20x6 — because a brand wordmark is typically 9:1
          and `object-contain` inside a square box scales to the WIDTH. The
          result was six illegible smudges, and no tile size fixes it: the
          geometry of a 9:1 mark in a circle cannot read at this scale.
          The stack now carries the brand's initials in its accent colour,
          which is legible at 32px and is the same fallback the brand cards,
          the editor's-pick band and the related-brands strip already use. The
          full-size marks for these exact six brands render immediately below
          in the editor's-pick band, where the tiles are wide enough for them. */}
      {miniStackBrands.length > 0 && (
        <div className="mt-6 inline-flex items-center gap-4 rounded-full border border-gray-200 bg-white py-2.5 pl-3 pr-5 shadow-[0_12px_30px_-16px_rgba(0,0,0,0.12)]">
          <div className="flex items-center pl-[10px]">
            {miniStackBrands.slice(0, 6).map((b, i) => (
              <div
                key={b.slug}
                className="-ml-2.5 grid h-8 w-8 place-items-center overflow-hidden rounded-full border border-gray-200 bg-white shadow-[0_2px_6px_rgba(0,0,0,0.04)]"
                style={{ zIndex: 10 - i }}
                title={b.name}
              >
                <span
                  aria-hidden
                  className="text-[11px] font-extrabold leading-none tracking-[-0.02em]"
                  style={{ color: accentColorFor(b.slug) }}
                >
                  {deriveInitials(b.name)}
                </span>
              </div>
            ))}
          </div>
          <div className="text-[13px] font-medium text-gray-600">
            <strong className="font-extrabold text-gray-900">{totalCount.toLocaleString()}</strong>{" "}
            brands tracked ·{" "}
            <span className="font-bold text-[#0469f0]">updated regularly</span>
          </div>
        </div>
      )}
    </section>
  )
}
